import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Map as MapIcon, RectangleVertical, Square, RectangleHorizontal, Search, X } from 'lucide-react';
import { APIProvider, Map as GoogleMap, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { areaCenters, getRiskLevelFromColor, riskConfig } from './constants';

const NB_M = ['jan','feb','mar','apr','mai','jun','jul','aug','sep','okt','nov','des'];
const fmtD = (s) => { if (!s) return ''; const d = new Date(s); return isNaN(d) ? s : `${d.getDate()}. ${NB_M[d.getMonth()]}`; };
const fmtDateFull = (s) => { if (!s) return ''; const d = new Date(s); return isNaN(d) ? s : `${d.getDate()}. ${NB_M[d.getMonth()]} ${d.getFullYear()}`; };
const getWeek = (s) => { const d = new Date(s); return Math.ceil((((d - new Date(d.getFullYear(),0,1)) / 86400000) + new Date(d.getFullYear(),0,1).getDay() + 1) / 7); };

export default function MapSection({
  mapLocations,
  selectedMapMarker,
  setSelectedMapMarker,
  hoveredMapMarker,
  setHoveredMapMarker,
  selectedMapArea,
  availableMapAreas,
  selectedAreas,
  setSelectedAreas,
  selectedBulkId,
  setSelectedBulkId,
  availableBulks,
  allBulks,
  sampleDate
}) {
  // Areas that work better in landscape mode
  const LANDSCAPE_AREAS = ['P04 YSSF/FHN', 'P04 YSSF', 'P04'];
  
  const getDefaultLayout = (areas) => {
    if (!areas || areas.length === 0) return 'portrait';
    return areas.some(a => LANDSCAPE_AREAS.some(la => a.includes(la))) ? 'landscape' : 'portrait';
  };
  
  const [mapLayout, setMapLayout] = useState(() => getDefaultLayout(selectedAreas));
  const [locSearchOpen, setLocSearchOpen] = useState(false);
  const [locSearchQuery, setLocSearchQuery] = useState('');
  const [panelAreaFilter, setPanelAreaFilter] = useState(null); // null = auto-set to first area
  const [colorFilter, setColorFilter] = useState('all'); // top-level color filter
  
  // Auto-switch layout when area selection changes
  useEffect(() => {
    setMapLayout(getDefaultLayout(selectedAreas));
  }, [selectedAreas]);

  // Auto-set panel area filter to first available area
  useEffect(() => {
    if (panelAreaFilter === null && mapLocations.length > 0) {
      const areas = [...new Set(mapLocations.map(l => l.area || 'Ukjent'))];
      if (areas.length > 0) setPanelAreaFilter(areas[0]);
    }
  }, [mapLocations, panelAreaFilter]);

  const mapHeight = mapLayout === 'portrait' ? '800px' : mapLayout === 'square' ? '600px' : '450px';
  const showSidePanel = mapLayout !== 'landscape';

  return (
    <Card className="border-slate-200 shadow-md hover:shadow-lg transition-shadow mb-6">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <MapIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-bold text-slate-900">Stasjonskart</CardTitle>
              <p className="text-xs text-slate-500">
                Oversikt over overvåkingsstasjonar
                {sampleDate && <span className="ml-1.5 text-teal-600 font-medium">· {sampleDate}</span>}
              </p>
            </div>
          </div>
          {/* Layout toggle */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="map-layout-toggle">
            <button
              onClick={() => setMapLayout('portrait')}
              className={`p-1.5 rounded-md transition-all ${mapLayout === 'portrait' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-400 hover:text-slate-600'}`}
              title="Portrett"
            >
              <RectangleVertical className="w-4 h-4" />
            </button>
            <button
              onClick={() => setMapLayout('square')}
              className={`p-1.5 rounded-md transition-all ${mapLayout === 'square' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-400 hover:text-slate-600'}`}
              title="Kvadratisk"
            >
              <Square className="w-4 h-4" />
            </button>
            <button
              onClick={() => setMapLayout('landscape')}
              className={`p-1.5 rounded-md transition-all ${mapLayout === 'landscape' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-400 hover:text-slate-600'}`}
              title="Landskap"
            >
              <RectangleHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>
      </CardHeader>

      {/* Area + bulk selectors */}
      {selectedAreas && (
        <div className="px-4 pb-2 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => setSelectedAreas([])}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                selectedAreas.length === 0 ? 'bg-teal-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
              }`}
            >
              Alle områder
            </button>
            {(availableMapAreas || []).map(area => {
              const isSelected = selectedAreas.includes(area);
              return (
                <button
                  key={area}
                  onClick={() => {
                    setSelectedAreas(prev =>
                      isSelected ? prev.filter(a => a !== area) : [...prev, area]
                    );
                    if (setSelectedBulkId) setSelectedBulkId('latest');
                  }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                    isSelected ? 'bg-teal-600 text-white shadow-sm' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {area}
                </button>
              );
            })}
          </div>
          {setSelectedBulkId && (
            <>
              <div className="w-px h-5 bg-slate-200 hidden sm:block" />
              <Select value={selectedBulkId} onValueChange={setSelectedBulkId}>
                <SelectTrigger className="w-[220px] h-8 text-xs border-slate-200 bg-white shadow-sm" data-testid="bulk-selector">
                  <SelectValue placeholder="Velg prøve" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Siste prøve (alle)</SelectItem>
                  {(availableBulks || []).map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      {fmtDateFull(b.date)} — {b.area_name} (Uke {getWeek(b.date)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          {/* Color filter */}
          {mapLocations.length > 0 && (() => {
            const cp = { green: 0, yellow: 1, orange: 2, red: 3 };
            const getWorst = l => cp[l.maxColor || 'green'] >= cp[l.zooColor || 'green'] ? (l.maxColor || 'green') : (l.zooColor || 'green');
            const colors = [
              { key: 'yellow', label: 'Gul', dot: 'bg-yellow-400', active: 'bg-yellow-400' },
              { key: 'orange', label: 'Oransje', dot: 'bg-orange-400', active: 'bg-orange-400' },
              { key: 'red', label: 'Raud', dot: 'bg-red-500', active: 'bg-red-500' },
            ].filter(f => mapLocations.some(l => getWorst(l) === f.key));
            if (colors.length === 0) return null;
            return (
              <>
                <div className="w-px h-5 bg-slate-200 hidden sm:block" />
                <div className="flex items-center gap-1">
                  {colors.map(f => {
                    const count = mapLocations.filter(l => getWorst(l) === f.key).length;
                    const isActive = colorFilter === f.key;
                    return (
                      <button
                        key={f.key}
                        onClick={() => setColorFilter(prev => prev === f.key ? 'all' : f.key)}
                        className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                          isActive ? `${f.active} text-white shadow-sm` : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className={`w-2.5 h-2.5 rounded-full ${isActive ? 'bg-white' : f.dot}`} />
                        {f.label} ({count})
                      </button>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      )}

      <CardContent className="p-3 lg:p-4">
        <div className={`flex gap-4 ${showSidePanel ? 'flex-row' : 'flex-col'}`} style={mapLayout === 'square' ? { alignItems: 'stretch' } : undefined}>
          {/* Map */}
          <div
            className={`rounded-xl overflow-hidden border border-slate-200 ${mapLayout === 'landscape' ? 'w-full' : ''}`}
            style={{
              ...(mapLayout === 'square' ? { flex: 1, minHeight: '500px' } : { height: mapHeight }),
              ...(mapLayout === 'portrait' ? { width: '55%', minWidth: '400px' } : {}),
            }}
          >
            <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
              <GoogleMap
                key={`admin-map-${selectedMapArea}-${mapLayout}`}
                mapId="admin-dashboard-map"
                defaultCenter={areaCenters[selectedMapArea]?.lat ? { lat: areaCenters[selectedMapArea].lat, lng: areaCenters[selectedMapArea].lng } : { lat: 60.0, lng: 6.5 }}
                defaultZoom={areaCenters[selectedMapArea]?.zoom || 7}
                mapTypeId="terrain"
                disableDefaultUI={false}
                zoomControl={true}
                gestureHandling="cooperative"
                style={{ width: '100%', height: '100%' }}
              >
                {/* Markers — filtered by color filter */}
                {mapLocations.filter(loc => {
                  if (colorFilter === 'all') return true;
                  const cp = { green: 0, yellow: 1, orange: 2, red: 3 };
                  const worst = cp[loc.maxColor || 'green'] >= cp[loc.zooColor || 'green'] ? (loc.maxColor || 'green') : (loc.zooColor || 'green');
                  return worst === colorFilter;
                }).map((loc, index) => {
                  const algaeRiskLevel = getRiskLevelFromColor(loc.maxColor || 'green');
                  const algaeConfig = riskConfig[algaeRiskLevel];
                  const zooRiskLevel = getRiskLevelFromColor(loc.zooColor || 'green');
                  const zooConfig = riskConfig[zooRiskLevel];
                  const isSelected = selectedMapMarker === loc.name;
                  const isHovered = hoveredMapMarker === loc.name;
                  const letter = loc.letter || String.fromCharCode(65 + (index % 26));
                  const colorPriority = { 'green': 0, 'yellow': 1, 'orange': 2, 'red': 3 };
                  const worstColor = colorPriority[loc.maxColor || 'green'] >= colorPriority[loc.zooColor || 'green']
                    ? (loc.maxColor || 'green') : (loc.zooColor || 'green');
                  const worstConfig = riskConfig[getRiskLevelFromColor(worstColor)];

                  return (
                    <AdvancedMarker
                      key={`marker-${index}-${loc.name}`}
                      position={{ lat: loc.lat, lng: loc.lng }}
                      onClick={() => setSelectedMapMarker(isSelected ? null : loc.name)}
                      zIndex={isHovered ? 10000 : (isSelected ? 1000 : 100 + index)}
                    >
                      <div
                        className="relative cursor-pointer"
                        onMouseEnter={() => setHoveredMapMarker(loc.name)}
                        onMouseLeave={() => setHoveredMapMarker(null)}
                      >
                        <div className={`w-10 h-10 rounded-full ${worstConfig.bgClass} border-2 border-white shadow-lg flex items-center justify-center transition-transform duration-150 ease-out ${isHovered ? 'scale-110' : ''} ${isSelected ? 'scale-125 ring-4 ring-offset-2 ring-opacity-50 ' + (worstColor === 'red' ? 'ring-red-400' : worstColor === 'orange' ? 'ring-amber-400' : 'ring-emerald-400') : ''}`}>
                          <span className="text-white font-bold text-sm pointer-events-none drop-shadow-sm">{letter}</span>
                        </div>
                        {worstColor === 'red' && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full border-2 border-white flex items-center justify-center pointer-events-none">
                            <span className="text-white text-[8px] font-bold">!</span>
                          </div>
                        )}
                        {/* Hover tooltip */}
                        {isHovered && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 pointer-events-none" style={{ zIndex: 99999 }}>
                            <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-3 min-w-[200px] whitespace-nowrap">
                              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                <div className="border-8 border-transparent border-t-white drop-shadow-sm" />
                              </div>
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`w-8 h-8 rounded-full ${worstConfig.bgClass} flex items-center justify-center border border-white shadow-sm`}>
                                  <span className="text-white font-bold text-xs drop-shadow-sm">{letter}</span>
                                </div>
                                <div>
                                  <div className="font-bold text-slate-900 text-sm">{loc.fullName || loc.name}</div>
                                  <div className="text-[10px] text-slate-500">{loc.area || ''}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 mb-2">
                                <div className={`${algaeConfig.lightBg} rounded-lg px-2 py-1 flex-1 text-center`}>
                                  <span className={`text-[10px] font-semibold ${algaeConfig.textColor}`}>Alge: {algaeConfig.label}</span>
                                </div>
                                <div className={`${zooConfig.lightBg} rounded-lg px-2 py-1 flex-1 text-center`}>
                                  <span className={`text-[10px] font-semibold ${zooConfig.textColor}`}>Zoo: {zooConfig.label}</span>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-[10px]">
                                <div className="bg-slate-50 rounded px-2 py-1">
                                  <span className="text-slate-500">Prøver:</span>
                                  <span className="font-bold text-slate-700 ml-1">{loc.sampleCount}</span>
                                </div>
                                <div className="bg-slate-50 rounded px-2 py-1">
                                  <span className="text-slate-500">Dato:</span>
                                  <span className="font-bold text-slate-700 ml-1">{fmtD(loc.date)}</span>
                                </div>
                                {loc.temperature && (
                                  <div className="bg-sky-50 rounded px-2 py-1 col-span-2">
                                    <span className="text-slate-500">Temp:</span>
                                    <span className="font-bold text-sky-600 ml-1">{loc.temperature}°C</span>
                                  </div>
                                )}
                              </div>
                              <div className="text-[10px] text-slate-400 mt-2 text-center">Klikk for detaljer</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </AdvancedMarker>
                  );
                })}

                {/* InfoWindow */}
                {selectedMapMarker && mapLocations.find(l => l.name === selectedMapMarker) && (() => {
                  const loc = mapLocations.find(l => l.name === selectedMapMarker);
                  const algaeRisk = getRiskLevelFromColor(loc.maxColor || 'green');
                  const algaeConf = riskConfig[algaeRisk];
                  const zooRisk = getRiskLevelFromColor(loc.zooColor || 'green');
                  const zooConf = riskConfig[zooRisk];
                  const letter = loc.letter || String.fromCharCode(65 + mapLocations.indexOf(loc));
                  const cp = { 'green': 0, 'yellow': 1, 'orange': 2, 'red': 3 };
                  const worst = cp[loc.maxColor || 'green'] >= cp[loc.zooColor || 'green'] ? (loc.maxColor || 'green') : (loc.zooColor || 'green');
                  const worstConf = riskConfig[getRiskLevelFromColor(worst)];

                  return (
                    <InfoWindow position={{ lat: loc.lat, lng: loc.lng }} pixelOffset={[0, -30]} onCloseClick={() => setSelectedMapMarker(null)}>
                      <div className="min-w-[260px] p-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-base shadow-md ${worstConf.bgClass}`}>{letter}</div>
                          <div className="flex-1">
                            <h3 className="font-bold text-slate-900 text-base leading-tight">{loc.fullName || loc.name}</h3>
                            <p className="text-xs text-slate-500">{loc.area || ''} · {loc.lat?.toFixed(3)}°N, {loc.lng?.toFixed(3)}°E</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`${algaeConf.lightBg} border ${algaeConf.borderColor} rounded-lg px-3 py-2 flex-1 text-center`}>
                            <div className="text-[10px] text-slate-400 mb-0.5">Alge</div>
                            <span className={`font-semibold text-sm ${algaeConf.textColor}`}>{algaeConf.label}</span>
                          </div>
                          <div className={`${zooConf.lightBg} border ${zooConf.borderColor} rounded-lg px-3 py-2 flex-1 text-center`}>
                            <div className="text-[10px] text-slate-400 mb-0.5">Zooplankton</div>
                            <span className={`font-semibold text-sm ${zooConf.textColor}`}>{zooConf.label}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mb-3 text-sm">
                          <div className="bg-slate-50 rounded-lg px-3 py-2 flex-1">
                            <div className="text-slate-400 text-[10px]">Prøvedato</div>
                            <div className="font-bold text-slate-800">
                              {fmtD(loc.date)}
                              {loc.temperature && <span className="ml-2 text-sky-600 font-semibold">{loc.temperature}°C</span>}
                            </div>
                          </div>
                          <div className="bg-slate-50 rounded-lg px-3 py-2 text-center">
                            <div className="text-slate-400 text-[10px]">Prøver</div>
                            <div className="font-bold text-slate-800">{loc.sampleCount}</div>
                          </div>
                        </div>
                        {loc.colorHistory && loc.colorHistory.length > 1 && (
                          <div className="mb-3">
                            <div className="text-[10px] text-slate-400 mb-1.5">Fargestatus føregåande veker</div>
                            <div className="flex items-center gap-1.5">
                              {loc.colorHistory.sort((a,b) => a.date.localeCompare(b.date)).filter(h => h.date !== loc.date).slice(-5).map((h, i) => {
                                const colorMap = { green: 'bg-emerald-500', yellow: 'bg-yellow-300', orange: 'bg-orange-400', red: 'bg-red-500' };
                                return (
                                  <div key={i} className="flex flex-col items-center gap-0.5">
                                    <div className={`w-5 h-5 rounded-full ${colorMap[h.color] || 'bg-slate-300'} border-2 border-white shadow-sm`} title={`${h.date}: ${h.color}`} />
                                    <span className="text-[8px] text-slate-400">{fmtD(h.date)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <a href={`/admin/analytics?location=${encodeURIComponent(loc.fullName || loc.name)}`}
                            className="flex-1 block text-center text-xs text-teal-600 hover:text-teal-700 font-medium py-2 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors">
                            Alge-diagram →
                          </a>
                          <a href="/admin/analytics"
                            className="flex-1 block text-center text-xs text-purple-600 hover:text-purple-700 font-medium py-2 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                            Zooplankton →
                          </a>
                        </div>
                      </div>
                    </InfoWindow>
                  );
                })()}
              </GoogleMap>
            </APIProvider>
          </div>

          {/* Side panel - in portrait and square modes */}
          {showSidePanel && (() => {
            const singleArea = selectedMapArea !== 'all';
            const fewLocations = mapLocations.length <= 14;
            const legendCard = (
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Fargenivå</h4>
                <div className="space-y-2">
                  {[
                    { color: 'bg-emerald-500', label: 'Låg', shortLabel: 'Grøn', tooltip: 'Mengder av plankton normalt ikkje assosiert med vesentlege effektar på fisk.' },
                    { color: 'bg-yellow-300', label: 'Moderat', shortLabel: 'Gul', tooltip: 'Mengder av plankton opp mot nivå der begynnande adferdsendringar/stressymptom på fisk kan inntreffe.' },
                    { color: 'bg-orange-400', label: 'Høg', shortLabel: 'Oransje', tooltip: 'Mengder av plankton der ein kan forvente stresseffektar på fisk.' },
                    { color: 'bg-red-500', label: 'Ekstrem', shortLabel: 'Raud', tooltip: 'Mengder av plankton der vesentleg dødelighet av fisk tidlegare er registrert.' },
                  ].map(l => (
                    <div key={l.label} className="flex items-center gap-2.5 relative group/tip cursor-help">
                      <div className={`w-3.5 h-3.5 rounded-full ${l.color} shrink-0`} />
                      <div>
                        <span className="text-xs font-medium text-slate-700">{l.label}</span>
                        <span className="text-[10px] text-slate-400 ml-1"> ({l.shortLabel})</span>
                      </div>
                      {/* Tooltip */}
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover/tip:block z-50 pointer-events-none" style={{ width: '240px' }}>
                        <div className="bg-slate-900 text-white text-[11px] leading-relaxed rounded-lg px-3 py-2 shadow-xl">
                          <div className="flex items-center gap-1.5 mb-1">
                            <div className={`w-2.5 h-2.5 rounded-full ${l.color} shrink-0`} />
                            <span className="font-semibold">{l.label} ({l.shortLabel})</span>
                          </div>
                          {l.tooltip}
                          <div className="absolute top-full left-4 border-[6px] border-transparent border-t-slate-900" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );

            return (
              <div className={`shrink-0 flex flex-col gap-3 ${mapLayout === 'portrait' ? 'flex-1 min-w-[250px]' : 'w-[280px]'}`} data-testid="map-side-panel">
                {/* Legend card removed from side panel - now always below */}

                {/* Location list */}
                {mapLocations.length > 0 && (
                  <div className="bg-slate-50 rounded-xl border border-slate-100 flex-1 overflow-hidden flex flex-col">
                    {/* Sticky header with column labels + search */}
                    <div className="flex items-center justify-between px-4 pt-4 pb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                          Stasjonar ({mapLocations.length})
                        </h4>
                        {locSearchOpen ? (
                          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-0.5">
                            <Search className="w-3 h-3 text-slate-400 shrink-0" />
                            <input
                              autoFocus
                              value={locSearchQuery}
                              onChange={(e) => setLocSearchQuery(e.target.value)}
                              placeholder="Søk..."
                              className="text-xs bg-transparent outline-none w-24 text-slate-700 placeholder:text-slate-300"
                            />
                            <button onClick={() => { setLocSearchOpen(false); setLocSearchQuery(''); }} className="text-slate-400 hover:text-slate-600">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => setLocSearchOpen(true)} className="p-1 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors" title="Søk stasjon">
                            <Search className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Area filter tabs */}
                    {(() => {
                      const panelAreas = [...new Set(mapLocations.map(l => l.area || 'Ukjent'))];
                      if (panelAreas.length <= 1) return null;
                      return (
                        <div className="flex items-center gap-1.5 px-4 pb-2 flex-wrap">
                          {panelAreas.map(area => {
                            const count = mapLocations.filter(l => (l.area || 'Ukjent') === area).length;
                            return (
                              <button
                                key={area}
                                onClick={() => setPanelAreaFilter(prev => prev === area ? 'all' : area)}
                                className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-all ${
                                  panelAreaFilter === area ? 'bg-teal-600 text-white shadow-sm' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                {area} ({count})
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                    {/* Scrollable list area */}
                    <div className="overflow-y-auto px-4 pb-4 flex-1">
                    <div className="space-y-0.5">
                      {(() => {
                        // Filter by panel area tab + top-level color filter + search
                        const filtered = mapLocations.filter(loc => {
                          if (panelAreaFilter && panelAreaFilter !== 'all' && (loc.area || 'Ukjent') !== panelAreaFilter) return false;
                          if (colorFilter !== 'all') {
                            const cp = { green: 0, yellow: 1, orange: 2, red: 3 };
                            const worst = cp[loc.maxColor || 'green'] >= cp[loc.zooColor || 'green'] ? (loc.maxColor || 'green') : (loc.zooColor || 'green');
                            if (worst !== colorFilter) return false;
                          }
                          if (!locSearchQuery) return true;
                          const q = locSearchQuery.toLowerCase();
                          const shortName = loc.name?.includes(' - ') ? loc.name.split(' - ').slice(1).join(' - ') : loc.name;
                          return shortName?.toLowerCase().includes(q) || loc.area?.toLowerCase().includes(q) || loc.letter?.toLowerCase().includes(q);
                        });
                        
                        // Group by area
                        const areas = [...new Set(filtered.map(l => l.area || 'Ukjent'))];
                        const showAreaHeaders = areas.length > 1;
                        
                        // Sort filtered by letter for consistent display order
                        filtered.sort((a, b) => (a.letter || 'Z').localeCompare(b.letter || 'Z'));
                        
                        const elements = [];
                        
                        // ALGE/ZOO column header - same layout as location rows for perfect alignment
                        elements.push(
                          <div key="col-header" className="flex items-center gap-2 px-2.5 py-0.5 text-xs">
                            <div className="w-6 shrink-0" />
                            <div className="flex-1" />
                            <div className="flex items-center gap-3 shrink-0 pr-0.5">
                              <span className="w-10 text-right text-[9px] font-semibold text-slate-400 uppercase tracking-wider">Alge</span>
                              <span className="w-10 text-right text-[9px] font-semibold text-slate-400 uppercase tracking-wider">Zoo</span>
                            </div>
                          </div>
                        );
                        
                        areas.forEach((area, areaIdx) => {
                          const areaLocs = filtered.filter(l => (l.area || 'Ukjent') === area);
                          if (areaLocs.length === 0) return;
                          
                          // Area separator when viewing multiple areas
                          if (showAreaHeaders) {
                            elements.push(
                              <div key={`area-${area}`} className={`flex items-center gap-2 px-1 ${areaIdx > 0 ? 'pt-2.5 mt-1 border-t border-slate-200' : ''} pb-1`}>
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{area}</span>
                                <span className="text-[9px] text-slate-400">({areaLocs.length})</span>
                              </div>
                            );
                          }
                          
                          areaLocs.forEach((loc, index) => {
                            const algaeConf = riskConfig[getRiskLevelFromColor(loc.maxColor || 'green')];
                            const zooConf = riskConfig[getRiskLevelFromColor(loc.zooColor || 'green')];
                            const worstConf = riskConfig[getRiskLevelFromColor(
                              ({ green: 0, yellow: 1, orange: 2, red: 3 }[loc.maxColor || 'green'] >= { green: 0, yellow: 1, orange: 2, red: 3 }[loc.zooColor || 'green'])
                                ? (loc.maxColor || 'green') : (loc.zooColor || 'green')
                            )];
                            const letter = loc.letter || String.fromCharCode(65 + (index % 26));
                            const isActive = selectedMapMarker === loc.name;
                            const shortName = loc.name?.includes(' - ') ? loc.name.split(' - ').slice(1).join(' - ') : loc.name;

                            elements.push(
                              <button
                                key={`side-${areaIdx}-${index}-${loc.name}`}
                                onClick={() => setSelectedMapMarker(isActive ? null : loc.name)}
                                onMouseEnter={() => setHoveredMapMarker(loc.name)}
                                onMouseLeave={() => setHoveredMapMarker(null)}
                                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition-all text-xs ${isActive ? 'bg-white shadow-sm ring-1 ring-slate-200' : 'hover:bg-white/70'}`}
                              >
                                <div className={`w-6 h-6 rounded-full ${worstConf.bgClass} flex items-center justify-center shrink-0`}>
                                  <span className="text-white font-bold text-[10px]">{letter}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-slate-700 truncate">{shortName}</div>
                                  {loc.temperature && (
                                    <div className="text-[10px] text-sky-600 font-medium">{loc.temperature}°C</div>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 shrink-0 pr-0.5">
                                  <span className={`w-10 text-right text-[10px] font-medium ${algaeConf.textColor}`}>{algaeConf.label}</span>
                                  <span className={`w-10 text-right text-[10px] font-medium ${zooConf.textColor}`}>{zooConf.label}</span>
                                </div>
                              </button>
                            );
                          });
                        });
                        
                        if (elements.length === 0 && locSearchQuery) {
                          return <p className="text-xs text-slate-400 text-center py-4">Ingen treff for "{locSearchQuery}"</p>;
                        }
                        
                        return elements;
                      })()}
                    </div>
                    </div>
                  </div>
                )}

                {/* Stats summary - only when viewing all areas */}
                {!singleArea && mapLocations.length > 0 && (() => {
                  const counts = { green: 0, yellow: 0, orange: 0, red: 0 };
                  mapLocations.forEach(l => { const c = l.maxColor || 'green'; if (counts[c] !== undefined) counts[c]++; });
                  return (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                      <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Samandrag</h4>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { c: 'emerald', n: counts.green, l: 'Grøn' },
                          { c: 'yellow', n: counts.yellow, l: 'Gul' },
                          { c: 'orange', n: counts.orange, l: 'Oransje' },
                          { c: 'red', n: counts.red, l: 'Raud' },
                        ].map(s => (
                          <div key={s.l} className="text-center">
                            <div className={`text-lg font-bold text-${s.c}-600 tabular-nums`}>{s.n}</div>
                            <div className="text-[10px] text-slate-500">{s.l}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </div>

        {/* Legend below map+panel row for all single-area views */}
        {showSidePanel && selectedMapArea !== 'all' && (
          <div className="mt-3 bg-slate-50 rounded-xl p-4 border border-slate-100">
            <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Fargenivå</h4>
            <div className="flex items-center gap-6">
              {[
                { color: 'bg-emerald-500', label: 'Låg', shortLabel: 'Grøn', tooltip: 'Mengder av plankton normalt ikkje assosiert med vesentlege effektar på fisk.' },
                { color: 'bg-yellow-300', label: 'Moderat', shortLabel: 'Gul', tooltip: 'Mengder av plankton opp mot nivå der begynnande adferdsendringar/stressymptom på fisk kan inntreffe.' },
                { color: 'bg-orange-400', label: 'Høg', shortLabel: 'Oransje', tooltip: 'Mengder av plankton der ein kan forvente stresseffektar på fisk.' },
                { color: 'bg-red-500', label: 'Ekstrem', shortLabel: 'Raud', tooltip: 'Mengder av plankton der vesentleg dødelighet av fisk tidlegare er registrert.' },
              ].map(l => (
                <div key={l.label} className="flex items-center gap-2 relative group/tip2 cursor-help">
                  <div className={`w-3.5 h-3.5 rounded-full ${l.color} shrink-0`} />
                  <span className="text-xs font-medium text-slate-700">{l.label}</span>
                  <span className="text-[10px] text-slate-400">({l.shortLabel})</span>
                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover/tip2:block z-50 pointer-events-none" style={{ width: '240px' }}>
                    <div className="bg-slate-900 text-white text-[11px] leading-relaxed rounded-lg px-3 py-2 shadow-xl">
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className={`w-2.5 h-2.5 rounded-full ${l.color} shrink-0`} />
                        <span className="font-semibold">{l.label} ({l.shortLabel})</span>
                      </div>
                      {l.tooltip}
                      <div className="absolute top-full left-4 border-[6px] border-transparent border-t-slate-900" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Station table + legend below map - only in landscape mode */}
        {mapLayout === 'landscape' && mapLocations.length > 0 && (
          <div className="mt-4 bg-slate-50 rounded-xl border border-slate-100 p-4">
            <div className="flex items-start gap-6">
              {/* Station grid with Alge/Zoo */}
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-2">
                  <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex-1">Stasjonar ({mapLocations.length})</h4>
                  <span className="text-[9px] font-semibold text-slate-400 uppercase w-10 text-center">Alge</span>
                  <span className="text-[9px] font-semibold text-slate-400 uppercase w-10 text-center">Zoo</span>
                </div>
                <div className="space-y-0.5">
                  {[...mapLocations].sort((a, b) => (a.letter || 'Z').localeCompare(b.letter || 'Z')).map((loc, index) => {
                    const algaeConf = riskConfig[getRiskLevelFromColor(loc.maxColor || 'green')];
                    const zooConf = riskConfig[getRiskLevelFromColor(loc.zooColor || 'green')];
                    const worstConf = riskConfig[getRiskLevelFromColor(
                      ({ green: 0, yellow: 1, orange: 2, red: 3 }[loc.maxColor || 'green'] >= { green: 0, yellow: 1, orange: 2, red: 3 }[loc.zooColor || 'green'])
                        ? (loc.maxColor || 'green') : (loc.zooColor || 'green')
                    )];
                    const letter = loc.letter || String.fromCharCode(65 + (index % 26));
                    const shortName = loc.name?.includes(' - ') ? loc.name.split(' - ').slice(1).join(' - ') : loc.name;
                    const isActive = selectedMapMarker === loc.name;
                    return (
                      <button
                        key={`loc-btn-${index}-${loc.name}`}
                        onClick={() => setSelectedMapMarker(isActive ? null : loc.name)}
                        onMouseEnter={() => setHoveredMapMarker(loc.name)}
                        onMouseLeave={() => setHoveredMapMarker(null)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${isActive ? 'bg-white shadow-sm ring-1 ring-slate-300' : 'hover:bg-white/80'}`}
                      >
                        <div className={`w-5 h-5 rounded-full ${worstConf.bgClass} flex items-center justify-center shrink-0`}>
                          <span className="text-white font-bold text-[9px]">{letter}</span>
                        </div>
                        <span className="font-medium text-slate-700 truncate flex-1 text-left min-w-0">{shortName}</span>
                        {loc.temperature && <span className="text-[9px] text-sky-600 font-medium shrink-0">{loc.temperature}°C</span>}
                        <span className={`w-10 text-center text-[10px] font-medium shrink-0 ${algaeConf.textColor}`}>{algaeConf.label}</span>
                        <span className={`w-10 text-center text-[10px] font-medium shrink-0 ${zooConf.textColor}`}>{zooConf.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="w-px self-stretch bg-slate-200" />

              {/* Color legend */}
              <div className="w-36 shrink-0 flex flex-col justify-center py-2">
                <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Fargenivå</h4>
                <div className="flex flex-col gap-3">
                  {[
                    { color: 'bg-emerald-500', label: 'Låg', desc: 'Normalt nivå' },
                    { color: 'bg-yellow-300', label: 'Moderat', desc: 'Auka mengder' },
                    { color: 'bg-orange-400', label: 'Høg', desc: 'Stresseffektar' },
                    { color: 'bg-red-500', label: 'Ekstrem', desc: 'Fare for dødelegheit' },
                  ].map(l => (
                    <div key={l.label} className="flex items-center gap-2.5">
                      <div className={`w-3.5 h-3.5 rounded-full ${l.color} shrink-0`} />
                      <div>
                        <div className="text-xs font-medium text-slate-700">{l.label}</div>
                        <div className="text-[9px] text-slate-400">{l.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
